// @ts-strict-ignore
import {Moment} from 'moment';

import {EventDefinition, LookupTableDefinition, PropertyDefinition} from 'apps/lexicon/src/models';
import {REPORTS, TABS} from 'apps/lexicon/src/constants';
import {FilterSelectConfig, FilterState, ParsedFilterSelectConfig} from 'common/components/filter-bar-table/types';
import {MixpanelUserItem} from 'common/widgets/user-list/types';
import {Metric} from 'app_api/projects/metrics/types';
import {DataGovernanceSettings} from 'apps/settings/src/api/project-types';
import {BehaviorEntity as Behavior} from 'app_api/projects/behaviors/types';

/** Represents "Users" or no Data Group in the UI. We would just use `null` but some controls can't match values if we do. */
export const DEFAULT_DATA_GROUP_ID: string = `$default_data_group_id`;

/* eslint-disable @typescript-eslint/naming-convention */
export const enum BulkActions {
  hide = `bulkHide`,
  unhide = `bulkUnhide`,
  dismissWarnings = `bulkDismissWarnings`,
  dismiss = `bulkDismiss`,
  drop = `bulkDrop`,
  undrop = `bulkUndrop`,
  mapToLookupTable = `bulkMapToLookupTable`,
  unMapFromLookupTable = `bulkunMapFromLookupTable`,
  upgradeToMapToLookupTable = `upgradeToMapToLookupTable`,
  merge = `bulkMerge`,
  markSensitive = `bulkMarkSensitive`,
  unmarkSensitive = `bulkUnmarkSensitive`,
  unmerge = `bulkUnmerge`,
  tag = `bulkTag`,
  delete = `bulkDelete`,
  download = `bulkDownload`,
  moreActionsOverflow = `dropdownAction`,
  editOwnership = `editOwnership`,
  verify = `bulkVerify`,
}

export type LexiconDefinition = EventDefinition | PropertyDefinition | LookupTableDefinition | Metric | Behavior;
export type IndexedEventDefinition = {[name: string]: EventDefinition};
export type IndexedPropertyDefinition = {[name: string]: PropertyDefinition};
export type IndexedLookupTableDefinition = {[name: string]: LookupTableDefinition};
export type IndexedMetricDefinition = {[id: Metric[`id`]]: Metric};
export type IndexedBehaviorDefinition = {[id: Behavior[`id`]]: Behavior};

interface Keyed {
  key: string;
}
export type KeyedEventDefinition = EventDefinition & Keyed;
export type KeyedPropertyDefinition = PropertyDefinition & Keyed;
export type KeyedMetricDefinition = Metric & Keyed;
export type KeyedBehaviorDefinition = Behavior & Keyed;
export type TabData = KeyedEventDefinition | KeyedPropertyDefinition | KeyedMetricDefinition | KeyedBehaviorDefinition;
export type SelectableRow = EventDefinition | PropertyDefinition;

export enum DataVolumeAnomalyStatus {
  Open = `open`,
  Dismissed = `dismissed`,
}

export type DataVolumeAnomaly = {
  id: number;
  event?: number; // event ID
  event_name?: string;
  property?: number; // property ID
  property_name?: string;
  timestamp: number | null;
  actual_count: number;
  predicted_upper: number;
  predicted_lower: number;
  percent_variance: string;
  status?: DataVolumeAnomalyStatus;
};

export enum EventFields {
  Name = `name`,
  DisplayName = `displayName`,
  Description = `description`,
  Status = `transformationStatus`,
  Volume = `volume30Days`,
  Queries = `queryCount`,
}

export enum EventTabs {
  YourEvents = `Your Events`,
  MixpanelEvents = `Mixpanel Events`,
}

export enum PropertyFields {
  Name = `name`,
  DisplayName = `displayName`,
  Description = `description`,
  Status = `status`,
  ExampleValue = `exampleValue`,
}

export enum PropertyTabs {
  YourProperties = `Your Properties`,
  MixpanelProperties = `Mixpanel Properties`,
}

export const enum IndividualActions {
  History = `history`,
}

export interface LexiconState {
  error: any;
  projectNotFound: boolean;
  dataGovernanceSettings: DataGovernanceSettings;
  eventDefinitions: IndexedEventDefinition;
  eventPropertiesDefinitions: IndexedPropertyDefinition;
  propertyDefinitions: IndexedPropertyDefinition;
  groupProfileSelected: string;
  isAutoTrackMagicLink: boolean;
  loading: any;
  loadingData: boolean;
  progressiveNumRows: number;
  previousTab: string;
  schema: any;
  showExportModal: boolean;
  showLookupTableUpsellModal: boolean;
  selectedEventName: string;
  selectedPropertyName: string;
  selectedFormulaId: string;
  selectedBehaviorId: string;
  selectedReport: typeof REPORTS;
  selectedTab: TABS;
  sortedColumn: {fieldName?: string; sortOrder?: number};
  tags: Set<string>;
  userPropertiesDefinitions: IndexedPropertyDefinition;
  groupPropertiesDefinitions: TODO; // TODO: (Akshay) - remember to create types
  lookupTablesDefinitions: IndexedLookupTableDefinition;
  replaceLookupTableUploadProgress: string;
  selectedUsersForBulkAction: string[];

  // New transformations
  filterState: FilterState;
  impactedRows: SelectableRow[];
  selectedRowDetailTab: unknown;
  singleRowEditing: string;
  loadingDetails: TODO;
  hasLoadedDetails: boolean;
  selectedRows: Set<string>;
  expandedRows: Set<string>;
  open: Partial<
    {
      [IndividualActions.History]:
        | {type: `event`; name: string}
        | {type: `property`; name: string; for_entity_type: `userprofile` | `event`; label?: string}
        | boolean;
      createNewLookupTable: boolean;
      dataDictionaryImportWizard: boolean;
    } & Record<BulkActions, boolean>
  >;
  filterSelectConfigs: FilterSelectConfig[];
  parsedFilterSelectConfigs?: ParsedFilterSelectConfig[];

  // Definition Details
  modal: string;
  selectedLookupTableId: string;
  deletingSelectedLookupTable: boolean;

  // Property/Events Details Shared keys
  statusValue: string | null;
  isPendingAutofocus: boolean;

  // PropertyDetails State
  customPropertyModalOpen: boolean;
  showDropModal: boolean;
  statusToUpdate: string | null;

  // PropertyDetailsEventsTable State
  eventCategorySelected: EventTabs;

  // EventDetails State
  propertyCategorySelected: PropertyTabs;

  // EventDetailsTable State
  firstTrackedTime: Moment;
  loadingFirstTracked: boolean;
  eventContacts: string[];
  projectUsers: MixpanelUserItem[];
  mediaMenuOpen: boolean;
  eventMediaPath: string;
  eventMediaType: string;
  eventDetailsStartTime: number;
  dataVolumeAnomalies: DataVolumeAnomaly[] | number[];

  // Should items be pre-sorted
  shouldPreSort: boolean;
}

export enum View {
  // Data management views
  Behaviors = `behaviors`,
  Cohorts = `cohorts`,
  CustomEvents = `custom-events`,
  CustomEventProperties = `custom-event-properties`,
  EventProperties = `event-properties`,
  Events = `events`,
  Formulas = `formulas`,
  LookupTables = `lookup-tables`,
  Metrics = `metrics`,
  ProfileProperties = `profile-properties`,
  CustomProfileProperties = `custom-profile-properties`,

  // Settings view
  EventApproval = `event-approval`,
  DataDeletion = `advanced-deletion`,
  DataRules = `data-rules`,
  VolumeMonitoring = `volume-monitoring`,
  Issues = `issues`,
}

export enum Status {
  Loading = `loading`,
  Success = `success`,
  Error = `error`,
}

export const LexiconEntitySpecialCharEscapeMap: Record<string, string> = {
  '\n': `$mp-nl`,
  '\t': `$mp-tab`,
  '\r': `$mp-cr`,
};
